import {
  BRAND_DISPLAY_NAME,
  BRAND_WORDMARK_ACCENT_SUFFIX,
} from '@streamlain/brand';
import type { AppBaseConfig } from '@streamlain/view-config';
import type { MouseEvent } from 'react';
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';

const configSchema = z.object({
  text: z.string().default(''),
  showNavigation: z.boolean().default(true),
});

interface FooterEntry {
  id: string;
  label: string;
  href: string;
}

function AppBaseFooterRenderer({
  config,
  mode,
  runtimeContext,
}: ViewComponentRenderProps) {
  const typedConfig = config as Partial<z.infer<typeof configSchema>>;
  const appBaseConfig = readAppBaseConfig(runtimeContext);
  const branding = readAppBaseBranding(runtimeContext);
  const isAuthoring = mode === 'builder' || mode === 'preview';
  const locale = runtimeContext?.locale;
  const entries =
    readBoolean(typedConfig.showNavigation, true) && appBaseConfig
      ? listFooterEntries(appBaseConfig, locale).slice(0, 6)
      : [];
  const text =
    readString(typedConfig.text).trim() ||
    `Copyright ${new Date().getFullYear()} ${branding.displayName}`;
  const legalEntries = listLegalEntries(appBaseConfig, locale);
  const contactEntries = listContactEntries(branding.displayName);

  return createElement(
    'footer',
    {
      className: styles.appBaseFooter,
      'data-sl-role': 'app-base-footer',
      'data-sl-footer-variant': 'classic',
    },
    createElement(
      'div',
      { className: styles.appBaseFooterShell },
      createElement(
        'div',
        { className: styles.appBaseFooterBrandColumn },
        createElement(
          'a',
          {
            href: '/',
            className: styles.appBaseFooterBrand,
            onClick: (event: MouseEvent<HTMLAnchorElement>) => {
              if (isAuthoring) event.preventDefault();
            },
          },
          createFooterLogo(branding),
          createElement(
            'span',
            { className: styles.appBaseFooterWordmark },
            branding.displayName === BRAND_DISPLAY_NAME &&
              BRAND_WORDMARK_ACCENT_SUFFIX
              ? [
                  BRAND_DISPLAY_NAME.slice(
                    0,
                    -BRAND_WORDMARK_ACCENT_SUFFIX.length
                  ),
                  createElement(
                    'span',
                    {
                      key: 'accent',
                      className: styles.appBaseFooterWordmarkAccent,
                    },
                    BRAND_WORDMARK_ACCENT_SUFFIX
                  ),
                ]
              : branding.displayName
          )
        ),
        createElement(
          'div',
          { className: styles.appBaseFooterTagline },
          createElement(
            'p',
            { className: styles.appBaseFooterTaglineText },
            'Strukturierte Inhalte, Daten und Workflows an einem Ort.'
          ),
          createElement('hr', { className: styles.appBaseFooterTaglineRule }),
          createElement(
            'p',
            { className: styles.appBaseFooterTaglineText },
            'Bereit fuer Teams, Wissen und Prozesse.'
          )
        )
      ),
      createFooterColumn({
        title: 'Navigation',
        entries,
        isAuthoring,
        ariaLabel: 'Footer navigation',
      }),
      createFooterColumn({
        title: 'Rechtliches',
        entries: legalEntries,
        isAuthoring,
        ariaLabel: 'Legal links',
      }),
      createFooterColumn({
        title: 'Kontakt',
        entries: contactEntries,
        isAuthoring,
        ariaLabel: 'Contact links',
      })
    ),
    createElement(
      'div',
      { className: styles.appBaseFooterBottom },
      createElement(
        'p',
        {
          className: styles.appBaseFooterText,
          'data-sl-field': 'footer-text',
        },
        text
      ),
      createElement(
        'a',
        {
          href: '/',
          className: styles.appBaseFooterBottomLink,
          onClick: (event: MouseEvent<HTMLAnchorElement>) => {
            if (isAuthoring) event.preventDefault();
          },
        },
        'Deutsch'
      )
    )
  );
}

interface AppBaseFooterBranding {
  displayName: string;
  logoUrl: string | null;
}

function createFooterLogo(branding: AppBaseFooterBranding) {
  return branding.logoUrl
    ? createElement('img', {
        src: branding.logoUrl,
        alt: branding.displayName,
        className: styles.appBaseFooterLogo,
      })
    : createElement(
        'span',
        {
          className: styles.appBaseFooterLogoFallback,
          'aria-label': branding.displayName,
          role: 'img',
        },
        branding.displayName.trim().charAt(0).toLocaleUpperCase() ||
          BRAND_DISPLAY_NAME.charAt(0).toLocaleUpperCase()
      );
}

function createFooterColumn(input: {
  title: string;
  entries: readonly FooterEntry[];
  isAuthoring: boolean;
  ariaLabel: string;
}) {
  const { title, entries, isAuthoring, ariaLabel } = input;
  return createElement(
    'nav',
    { className: styles.appBaseFooterColumn, 'aria-label': ariaLabel },
    createElement(
      'h3',
      { className: styles.appBaseFooterColumnHeading },
      title
    ),
    createElement(
      'ul',
      { className: styles.appBaseFooterLinks },
      ...entries.map(entry =>
        createElement(
          'li',
          { key: entry.id },
          createElement(
            'a',
            {
              href: entry.href,
              className: styles.appBaseFooterLink,
              onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                if (isAuthoring) event.preventDefault();
              },
            },
            entry.label
          )
        )
      )
    )
  );
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readAppBaseConfig(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): AppBaseConfig | null {
  const appBase = runtimeContext?.appBase;
  const raw =
    appBase && typeof appBase === 'object' && 'config' in appBase
      ? (appBase as { config?: unknown }).config
      : null;
  return raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as AppBaseConfig).pages) &&
    Array.isArray((raw as AppBaseConfig).navigation)
    ? (raw as AppBaseConfig)
    : null;
}

function readAppBaseBranding(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): AppBaseFooterBranding {
  const appBase = runtimeContext?.appBase;
  const raw =
    appBase && typeof appBase === 'object' && 'branding' in appBase
      ? (appBase as { branding?: unknown }).branding
      : null;
  const record =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null;
  const settings =
    record?.['settings'] &&
    typeof record['settings'] === 'object' &&
    !Array.isArray(record['settings'])
      ? (record['settings'] as Record<string, unknown>)
      : null;
  const displayName =
    typeof settings?.['displayName'] === 'string' &&
    settings['displayName'].trim()
      ? settings['displayName'].trim()
      : BRAND_DISPLAY_NAME;
  const logoUrl =
    typeof record?.['logoUrl'] === 'string' && record['logoUrl'].trim()
      ? record['logoUrl'].trim()
      : null;
  return { displayName, logoUrl };
}

function listFooterEntries(
  appBaseConfig: AppBaseConfig,
  locale?: string
): FooterEntry[] {
  const pagesById = new Map(appBaseConfig.pages.map(page => [page.id, page]));
  return [...appBaseConfig.navigation]
    .sort((a, b) => a.order - b.order)
    .filter(item => !item.parentId)
    .flatMap(item => {
      const page = item.pageId ? pagesById.get(item.pageId) : null;
      const href = page?.path ?? item.href;
      if (!href) return [];
      return [
        {
          id: item.id,
          label: resolveNavigationLabel(item, locale, page?.id ?? href),
          href,
        },
      ];
    });
}

function listLegalEntries(
  appBaseConfig: AppBaseConfig | null,
  locale?: string
): FooterEntry[] {
  const pagesByPath = new Map(
    (appBaseConfig?.pages ?? []).map(page => [page.path.toLowerCase(), page])
  );
  const pathForSlug = (slug: string, fallback: string) =>
    (appBaseConfig?.pages ?? []).find(
      page =>
        page.rowSelection?.type === 'fixed-slug' &&
        page.rowSelection.slug === slug
    )?.path ??
    pagesByPath.get(fallback)?.path ??
    fallback;
  const english = locale?.trim().toLowerCase().startsWith('en') ?? false;
  const candidates = [
    {
      id: 'imprint',
      label: english ? 'Legal Notice' : 'Impressum',
      path: pathForSlug('impressum', '/impressum'),
    },
    {
      id: 'privacy',
      label: english ? 'Privacy Policy' : 'Datenschutz',
      path: pathForSlug('datenschutz', '/datenschutz'),
    },
    { id: 'terms', label: english ? 'Terms' : 'AGB', path: '/agb' },
    { id: 'dpa', label: english ? 'DPA' : 'AVV', path: '/avv' },
  ];
  return candidates.map(candidate => ({
    id: candidate.id,
    label: candidate.label,
    href: pagesByPath.get(candidate.path)?.path ?? candidate.path,
  }));
}

function resolveNavigationLabel(
  item: AppBaseConfig['navigation'][number],
  locale: string | undefined,
  fallback: string
): string {
  const normalizedLocale = locale?.trim().toLowerCase();
  const baseLocale = normalizedLocale?.split(/[-_]/)[0];
  return (
    (normalizedLocale ? item.labelsI18n?.[normalizedLocale] : undefined) ??
    (baseLocale ? item.labelsI18n?.[baseLocale] : undefined) ??
    (item.labelFormula?.type === 'value'
      ? String(item.labelFormula.value)
      : fallback)
  );
}

function listContactEntries(displayName: string): FooterEntry[] {
  return [
    { id: 'contact', label: displayName, href: '/' },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      href: 'https://linkedin.com/company/streamlain',
    },
    { id: 'x', label: 'X', href: 'https://x.com/streamlain' },
  ];
}

export const AppBaseFooterComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.APP_BASE_FOOTER,
  type: 'navigation',
  name: 'AppBase Footer',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    'F'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    text: '',
    showNavigation: true,
  },
  render: AppBaseFooterRenderer,
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      'F Footer'
    );
  },
};
